import React, { useMemo, useState } from "react";
import {
  MagnifyingGlass, CaretLeft, CaretRight, X, Check, UsersThree,
} from "@phosphor-icons/react";

/**
 * Selector de usuarios con búsqueda + paginación + selección múltiple persistente.
 * Pensado para escalar a miles de usuarios sin perder selección al navegar entre páginas.
 *
 * Props:
 *  - users:    array de { id, name, email, role }
 *  - value:    array de ids seleccionados
 *  - onChange: (newIds) => void
 *  - disabled: bool
 *  - pageSize: número de filas por página (default 10)
 *  - testid:   prefijo para data-testid
 */
export default function UserPicker({
  users = [],
  value = [],
  onChange,
  disabled = false,
  pageSize = 10,
  testid = "user-picker",
}) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    if (!q.trim()) return users;
    const s = q.trim().toLowerCase();
    return users.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(s) ||
        (u.email || "").toLowerCase().includes(s) ||
        (u.id || "").toLowerCase().includes(s)
    );
  }, [users, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const slice = filtered.slice(safePage * pageSize, (safePage + 1) * pageSize);

  const selectedSet = useMemo(() => new Set(value), [value]);
  const selectedUsers = useMemo(
    () => users.filter((u) => selectedSet.has(u.id)),
    [users, selectedSet]
  );

  const toggle = (id) => {
    if (disabled) return;
    const next = selectedSet.has(id)
      ? value.filter((x) => x !== id)
      : [...value, id];
    onChange?.(next);
  };

  // toggle de página completa visible
  const allInPageSelected =
    slice.length > 0 && slice.every((u) => selectedSet.has(u.id));
  const togglePage = () => {
    if (disabled) return;
    if (allInPageSelected) {
      onChange?.(value.filter((id) => !slice.some((u) => u.id === id)));
    } else {
      const ids = new Set(value);
      slice.forEach((u) => ids.add(u.id));
      onChange?.(Array.from(ids));
    }
  };

  const clearAll = () => onChange?.([]);

  return (
    <div data-testid={testid}>
      {/* Header: chips de seleccionados + acciones */}
      <div className="mb-2 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-neutral-600 mb-1">
            Seleccionados ({selectedUsers.length})
          </div>
          {selectedUsers.length === 0 ? (
            <div className="text-xs text-neutral-400 italic">
              Ningún usuario seleccionado todavía.
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-chips`}>
              {selectedUsers.map((u) => (
                <span
                  key={u.id}
                  className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium border border-blue-100"
                  data-testid={`${testid}-chip-${u.id}`}
                >
                  {u.name}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => toggle(u.id)}
                      className="ml-1 p-0.5 hover:bg-blue-100 rounded"
                      title="Quitar"
                      data-testid={`${testid}-chip-remove-${u.id}`}
                    >
                      <X size={11} weight="bold" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
        {!disabled && selectedUsers.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-neutral-500 hover:text-rose-500"
            data-testid={`${testid}-clear`}
          >
            Limpiar
          </button>
        )}
      </div>

      {/* Tabla con buscador y paginación */}
      <div className="border border-neutral-200 rounded-md overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-200 bg-neutral-50">
          <MagnifyingGlass size={14} className="text-neutral-400" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
            placeholder="Buscar por nombre, correo o ID..."
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-neutral-400"
            data-testid={`${testid}-search`}
            disabled={disabled}
          />
          {q && (
            <button
              type="button"
              onClick={() => { setQ(""); setPage(0); }}
              className="text-neutral-400 hover:text-neutral-700"
              title="Limpiar"
            >
              <X size={14} />
            </button>
          )}
          <div className="text-[11px] text-neutral-500 ml-2 whitespace-nowrap">
            {filtered.length} resultado{filtered.length !== 1 ? "s" : ""}
          </div>
        </div>

        {slice.length === 0 ? (
          <div className="py-10 text-center text-neutral-400 text-sm">
            <UsersThree size={28} className="mx-auto mb-2 text-neutral-300" weight="duotone" />
            Sin coincidencias para "{q}".
          </div>
        ) : (
          <table className="w-full text-sm" data-testid={`${testid}-table`}>
            <thead>
              <tr className="bg-neutral-50/50 text-[11px] uppercase tracking-widest text-neutral-500 border-b border-neutral-100">
                <th className="w-10 pl-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={allInPageSelected}
                    onChange={togglePage}
                    disabled={disabled}
                    className="cursor-pointer accent-blue-600"
                    title="Seleccionar toda la página"
                    data-testid={`${testid}-toggle-page`}
                  />
                </th>
                <th className="px-3 py-2 text-left font-semibold">Usuario</th>
                <th className="px-3 py-2 text-left font-semibold">Correo</th>
                <th className="px-3 py-2 text-left font-semibold">Rol</th>
              </tr>
            </thead>
            <tbody>
              {slice.map((u) => {
                const checked = selectedSet.has(u.id);
                return (
                  <tr
                    key={u.id}
                    onClick={() => toggle(u.id)}
                    className={`border-b border-neutral-100 cursor-pointer hover:bg-neutral-50 transition-colors ${
                      checked ? "bg-blue-50/40" : ""
                    } ${disabled ? "pointer-events-none opacity-70" : ""}`}
                    data-testid={`${testid}-row-${u.id}`}
                  >
                    <td className="pl-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(u.id)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={disabled}
                        className="cursor-pointer accent-blue-600"
                        data-testid={`${testid}-check-${u.id}`}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-md bg-gradient-to-br from-rose-500 to-amber-500 text-white flex items-center justify-center font-bold text-xs">
                          {(u.name || "?")[0]?.toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate">{u.name}</div>
                          <div className="text-[10px] text-neutral-400 font-mono truncate">{u.id?.slice(0, 8)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-neutral-600 text-xs">{u.email}</td>
                    <td className="px-3 py-2.5">
                      <span className="px-2 py-0.5 bg-neutral-100 text-neutral-700 rounded text-[11px] font-medium">
                        {u.role}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Paginación */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-neutral-100 bg-neutral-50/50 text-xs text-neutral-600" data-testid={`${testid}-pagination`}>
            <div>
              Página <strong>{safePage + 1}</strong> de {totalPages}
              <span className="text-neutral-400 ml-2">
                ({safePage * pageSize + 1}-{Math.min(filtered.length, (safePage + 1) * pageSize)} de {filtered.length})
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
                className="p-1.5 rounded-md hover:bg-neutral-200 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid={`${testid}-prev`}
              >
                <CaretLeft size={14} weight="bold" />
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={safePage >= totalPages - 1}
                className="p-1.5 rounded-md hover:bg-neutral-200 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid={`${testid}-next`}
              >
                <CaretRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
